/**
 * Centralized Query Keys for React Query
 *
 * This file defines all query keys used in the application.
 * Using a consistent query key structure makes it easy to:
 * - Invalidate specific queries after mutations
 * - Prefetch data
 * - Debug cache issues
 *
 * Key Structure Pattern:
 * - Base: ['resource'] - e.g., ['valuations']
 * - Lists: ['resource', 'list'] - e.g., ['valuations', 'list']
 * - Detail: ['resource', 'detail', id] - e.g., ['valuations', 'detail', '123']
 * - Filtered: ['resource', 'list', {filters}] - e.g., ['valuations', 'list', {status: 'active'}]
 *
 * Usage:
 * ```typescript
 * import { queryKeys } from '@/lib/platform/hooks/queries/query-keys';
 *
 * // In a query
 * useQuery({
 *   queryKey: queryKeys.valuations.detail('123'),
 *   queryFn: () => fetchValuation('123'),
 * });
 *
 * // Invalidate all valuations
 * queryClient.invalidateQueries({ queryKey: queryKeys.valuations.all });
 *
 * // Invalidate specific valuation
 * queryClient.invalidateQueries({ queryKey: queryKeys.valuations.detail('123') });
 * ```
 */

/**
 * Type for query key filters
 * Allows string, number, boolean, or nested objects
 */
type QueryKeyFilters = Record<string, string | number | boolean | Record<string, unknown>>;

// Timestamp: 2026-02-04 - Force Turbopack recompilation
export const queryKeys = {
  // ===========================================================================
  // VALUATIONS
  // ===========================================================================
  valuations: {
    all: ['valuations'] as const,
    lists: () => [...queryKeys.valuations.all, 'list'] as const,
    list: (filters?: QueryKeyFilters) =>
      filters
        ? ([...queryKeys.valuations.lists(), filters] as const)
        : queryKeys.valuations.lists(),
    details: () => [...queryKeys.valuations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.valuations.details(), id] as const,
    // Multi-entry media lines (valuation_media_entries) — nested under detail
    // so invalidating detail(id) also invalidates the entry lines.
    mediaEntries: (id: string) => [...queryKeys.valuations.detail(id), 'media-entries'] as const,
  },

  // ===========================================================================
  // VALUATION STATUS HISTORY
  // ===========================================================================
  valuationStatusHistory: {
    all: ['valuationStatusHistory'] as const,
    byId: (valuationId: string) => [...queryKeys.valuationStatusHistory.all, valuationId] as const,
  },

  // ===========================================================================
  // ORGANIZATIONS (Better Auth)
  // ===========================================================================
  organization: {
    all: ['organization'] as const,
    details: () => [...queryKeys.organization.all, 'detail'] as const,
    detail: (organizationId?: string | null) =>
      organizationId
        ? ([...queryKeys.organization.details(), organizationId] as const)
        : ([...queryKeys.organization.details(), 'default'] as const),
  },

  // ===========================================================================
  // CLUBS (ORGANIZATIONS)
  // ===========================================================================
  clubs: {
    all: ['clubs'] as const,
    lists: () => [...queryKeys.clubs.all, 'list'] as const,
    list: (filters?: QueryKeyFilters) =>
      filters ? ([...queryKeys.clubs.lists(), filters] as const) : queryKeys.clubs.lists(),
    details: () => [...queryKeys.clubs.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.clubs.details(), id] as const,
  },

  // ===========================================================================
  // USERS
  // ===========================================================================
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (organizationId?: string) =>
      organizationId
        ? ([...queryKeys.users.lists(), { organizationId }] as const)
        : queryKeys.users.lists(),
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    current: () => [...queryKeys.users.all, 'current'] as const,
  },

  // ===========================================================================
  // PERMISSIONS
  // ===========================================================================
  permissions: {
    all: ['permissions'] as const,
    user: (userId: string, organizationId: string) =>
      [...queryKeys.permissions.all, userId, organizationId] as const,
  },

  // ===========================================================================
  // API KEYS
  // ===========================================================================
  apiKeys: {
    all: ['apiKeys'] as const,
    lists: () => [...queryKeys.apiKeys.all, 'list'] as const,
    organization: (organizationId: string) =>
      [...queryKeys.apiKeys.lists(), organizationId] as const,
  },

  // ===========================================================================
  // MCP SETTINGS
  // ===========================================================================
  mcpSettings: {
    all: ['mcpSettings'] as const,
    org: (organizationId: string) => [...queryKeys.mcpSettings.all, organizationId] as const,
  },

  // ===========================================================================
  // PLUGIN SETTINGS
  // ===========================================================================
  pluginSettings: {
    all: ['pluginSettings'] as const,
    lists: () => [...queryKeys.pluginSettings.all, 'list'] as const,
    org: (organizationId: string) => [...queryKeys.pluginSettings.lists(), organizationId] as const,
    plugin: (organizationId: string, pluginId: string) =>
      [...queryKeys.pluginSettings.org(organizationId), pluginId] as const,
  },

  // ===========================================================================
  // INVITATIONS
  // ===========================================================================
  invitations: {
    all: ['invitations'] as const,
    lists: () => [...queryKeys.invitations.all, 'list'] as const,
    pending: () => [...queryKeys.invitations.lists(), 'pending'] as const,
    org: (organizationId: string) => [...queryKeys.invitations.lists(), organizationId] as const,
  },

  // ===========================================================================
  // MEMBERSHIPS
  // ===========================================================================
  memberships: {
    all: ['memberships'] as const,
    lists: () => [...queryKeys.memberships.all, 'list'] as const,
    user: (userId: string) => [...queryKeys.memberships.lists(), userId] as const,
    org: (organizationId: string) =>
      [...queryKeys.memberships.lists(), { organizationId }] as const,
  },

  // ===========================================================================
  // NEWSLETTER SUBSCRIBERS
  // ===========================================================================
  newsletter: {
    all: ['newsletter'] as const,
    subscribers: () => [...queryKeys.newsletter.all, 'subscribers'] as const,
    subscribersPaginated: (page: number) =>
      [...queryKeys.newsletter.subscribers(), { page }] as const,
  },

  // ===========================================================================
  // ADMIN INSIGHTS (Insights Engine, super admin only)
  // ===========================================================================
  adminInsights: {
    all: ['adminInsights'] as const,
    overview: () => [...queryKeys.adminInsights.all, 'overview'] as const,
  },

  // ===========================================================================
  // AUDIT LOGS
  // ===========================================================================
  auditLogs: {
    all: ['auditLogs'] as const,
    lists: () => [...queryKeys.auditLogs.all, 'list'] as const,
    org: (organizationId: string, page?: number) =>
      page
        ? ([...queryKeys.auditLogs.lists(), organizationId, { page }] as const)
        : ([...queryKeys.auditLogs.lists(), organizationId] as const),
  },

  // ===========================================================================
  // API USAGE LOGS
  // ===========================================================================
  apiUsage: {
    all: ['apiUsage'] as const,
    lists: () => [...queryKeys.apiUsage.all, 'list'] as const,
    organization: (organizationId: string, dateRange?: { start: string; end: string }) =>
      dateRange
        ? ([...queryKeys.apiUsage.lists(), organizationId, dateRange] as const)
        : ([...queryKeys.apiUsage.lists(), organizationId] as const),
  },

  // ===========================================================================
  // SPONSORSHIPS
  // ===========================================================================
  sponsorships: {
    all: ['sponsorships'] as const,
    sponsors: (organizationId: string) =>
      [...queryKeys.sponsorships.all, 'sponsors', organizationId] as const,
    packages: (organizationId: string) =>
      [...queryKeys.sponsorships.all, 'packages', organizationId] as const,
    contacts: (organizationId: string) =>
      [...queryKeys.sponsorships.all, 'contacts', organizationId] as const,
    analytics: (organizationId: string, period: string) =>
      [...queryKeys.sponsorships.all, 'analytics', organizationId, period] as const,
  },

  // ===========================================================================
  // DOCUMENTS
  // ===========================================================================
  documents: {
    all: ['documents'] as const,
    lists: () => [...queryKeys.documents.all, 'list'] as const,
    org: (organizationId: string) => [...queryKeys.documents.lists(), organizationId] as const,
    uploaded: (organizationId: string) =>
      [...queryKeys.documents.org(organizationId), 'uploaded'] as const,
    templates: (organizationId: string) =>
      [...queryKeys.documents.org(organizationId), 'templates'] as const,
  },

  // ===========================================================================
  // DOCUMENT FOLDERS
  // ===========================================================================
  documentFolders: {
    all: ['documentFolders'] as const,
    lists: () => [...queryKeys.documentFolders.all, 'list'] as const,
    org: (organizationId: string) =>
      [...queryKeys.documentFolders.lists(), organizationId] as const,
  },

  // ===========================================================================
  // CPM BENCHMARKS
  // ===========================================================================
  cpmBenchmarks: {
    all: ['cpmBenchmarks'] as const,
    lists: () => [...queryKeys.cpmBenchmarks.all, 'list'] as const,
    list: (filters?: QueryKeyFilters) =>
      filters
        ? ([...queryKeys.cpmBenchmarks.lists(), filters] as const)
        : queryKeys.cpmBenchmarks.lists(),
    details: () => [...queryKeys.cpmBenchmarks.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.cpmBenchmarks.details(), id] as const,
  },
};
